import { bnPageHead } from "@/lib/bn-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/content/site";
import portrait from "@/assets/tamal-portrait-photo.webp";
import { ArrowUpRight } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { buildAboutGraph } from "@/lib/about-schema";
import {
  BN_PILLARS,
  BN_WHY,
  BN_SKILLS,
  BN_ABOUT_SERVICES,
  BN_ABOUT_FAQ,
  BN_APPROACH,
} from "@/content/bn/about-content";
import { ABOUT_REVIEWED_ON } from "@/content/about-content";
import { AiSearchCards } from "@/components/about/AiSearchCards";
import { BN_AI_SEARCH_POINTS } from "@/content/bn/about-content";
import { FaqCardGrid } from "@/components/FaqCardGrid";
import { CtaPanel } from "@/components/CtaPanel";
import {
  AboutContactCard,
  AboutServiceGrid,
  ApproachCards,
  PillarGrid,
  SkillsList,
  WhyChecklist,
} from "@/components/about/AboutSections";


const TITLE = "পরিচিতি — ফ্রিল্যান্সার তামাল, রংপুরের এসইও ও AEO এক্সপার্ট";
const DESC =
  "রংপুর, বাংলাদেশের সিনিয়র এসইও, AEO ও GEO কনসালট্যান্ট ফ্রিল্যান্সার তামালের পরিচিতি — টেকনিক্যাল এসইও, লোকাল এসইও, Shopify এসইও ও AI সার্চ অপটিমাইজেশনের অভিজ্ঞতা।";

export const Route = createFileRoute("/bn/about")({
  head: () => ({
    ...bnPageHead({
      path: "/bn/about",
      enPath: "/about",
      title: TITLE,
      desc: DESC,
      keywords: ["এসইও এক্সপার্ট রংপুর", "সেরা এসইও এক্সপার্ট বাংলাদেশ", "ফ্রিল্যান্সার তামাল পরিচিতি"],
      type: "profile",
      meta: [
        { name: "author", content: SITE.fullName },
        { name: "geo.region", content: "BD-55" },
        { name: "geo.placename", content: "Rangpur, Bangladesh" },
        { name: "geo.position", content: "25.7439;89.2752" },
        { name: "ICBM", content: "25.7439, 89.2752" },
      ],
      og: [
        { property: "profile:first_name", content: "Tamal" },
        { property: "profile:username", content: "freelancertamal" },
      ],
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildAboutGraph(ABOUT_REVIEWED_ON, {
            path: "/bn/about",
            language: "bn",
            name: TITLE,
            description: DESC,
            breadcrumb: [
              { name: "হোম", path: "/bn" },
              { name: "পরিচিতি", path: "/bn/about" },
            ],
            faq: BN_ABOUT_FAQ,
          }),
        ),
      },
    ],
  }),
  component: BnAbout,
});

function BnAbout() {
  return (
    <>
      <Breadcrumbs items={[{ name: "হোম", path: "/bn" }, { name: "পরিচিতি", path: "/bn/about" }]} />

      <Section eyebrow="ফ্রিল্যান্সার তামাল সম্পর্কে">
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.15] -mt-10 mb-12 max-w-4xl">
          <span className="text-gradient">রংপুর, বাংলাদেশের</span> সিনিয়র এসইও এক্সপার্ট — টেকনিক্যাল এসইও, কনটেন্ট ও AI সার্চ।
        </h1>
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <Reveal className="lg:col-span-3">
            <p className="text-xl text-muted-foreground leading-relaxed">
              আমি <strong className="text-foreground">ফ্রিল্যান্সার তামাল</strong> — রংপুরভিত্তিক এসইও কনসালট্যান্ট।
              সার্চ ইঞ্জিন অপটিমাইজেশনের মাধ্যমে অনলাইন দৃশ্যমানতা উন্নত করার পরিকল্পনা করি।
              লোকাল এসইও, টেকনিক্যাল এসইও — নৈতিক পদ্ধতিতে, মাপা যায় এমন ফলাফলসহ।
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/bn/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90">
                যোগাযোগ করুন <ArrowUpRight size={16} />
              </Link>
              <Link to="/bn/services" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card">
                সেবাসমূহ দেখুন
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="rounded-3xl overflow-hidden border border-border aspect-[4/5] relative">
              <img
                src={portrait}
                alt="ফ্রিল্যান্সার তামাল — রংপুর, বাংলাদেশের সিনিয়র এসইও এক্সপার্ট"
                width={800}
                height={1000}
                loading="eager"
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section eyebrow="আমি যা নিয়ে আসি" title={<>আমার কাজের <span className="text-gradient">চারটি স্তম্ভ।</span></>}>
        <PillarGrid items={BN_PILLARS} headingLevel="h2" />
      </Section>

      <Section eyebrow="কেন আমি" title={<>কেন <span className="text-gradient">ফ্রিল্যান্সার তামাল?</span></>}>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="text-lg text-muted-foreground leading-relaxed">
              আমার পরামর্শসেবার মূল ক্ষেত্র টেকনিক্যাল এসইও, লোকাল সার্চ, ই-কমার্স ও AI সার্চের প্রস্তুতি।
              আপনার সাইটের প্রকৃত সমস্যা ও দর্শকের প্রশ্ন অনুযায়ী অগ্রাধিকার নির্ধারিত হয়।
              ফলাফলের দাবি করতে তারিখসহ সংশ্লিষ্ট সাইটের প্রমাণ দরকার।
            </p>
          </Reveal>
          <WhyChecklist items={BN_WHY} />
        </div>
      </Section>

      <Section eyebrow="পরিচয়" title={<>রংপুরের <span className="text-gradient">এসইও কনসালট্যান্ট।</span></>}>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                রংপুর থেকে টেকনিক্যাল এসইও, লোকাল সার্চ ও কনটেন্ট পরিকল্পনা করি।
                ব্যবসা, দর্শক ও উপলব্ধ প্রমাণ অনুযায়ী পদ্ধতি বাছি — নির্দিষ্ট র‍্যাঙ্কিং বা সময়সীমার গ্যারান্টি নয়।
              </p>
              <p className="text-foreground italic">
                "মান নিয়ে আপস করি না। ক্লায়েন্ট কাজটা পছন্দ না করলে আমিও করি না — সেভাবে এগোই না।"
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <AboutContactCard
              facts={[
                { label: "কাজের ক্ষেত্র", value: "এসইও ও AI সার্চ" },
                { label: "অবস্থান", value: "রংপুর, বাংলাদেশ" },
              ]}
            />
          </Reveal>
        </div>
      </Section>

      <Section eyebrow="দক্ষতা" title={<>ডিজিটাল মার্কেটিংয়ে <span className="text-gradient">আমার পথচলা।</span></>}>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                প্রযুক্তির প্রতি টান আর মানুষের সঙ্গে কাজ করার আগ্রহ — এই দুটোই আমাকে ডিজিটাল মার্কেটিংয়ে টেনে আনে।
                এসইও ও তার টুলগুলো শিখতে শুরু করি, আর দ্রুতই বুঝি এগুলো একটা ব্যবসার
                গতিপথ কতটা বদলে দিতে পারে।
              </p>
              <p>
                প্রজেক্টের পর প্রজেক্টে লোকাল এসইও, কীওয়ার্ড গবেষণা, টেকনিক্যাল অডিট আর এখন AEO/GEO-তে দক্ষতা
                গড়ে উঠেছে — সবসময় বাস্তব ডেটার ওপর দাঁড়িয়ে।
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SkillsList skills={BN_SKILLS} />
          </Reveal>
        </div>
      </Section>

      <Section eyebrow="সেবাসমূহ" title={<>শুরু থেকে শেষ পর্যন্ত <span className="text-gradient">পূর্ণাঙ্গ এসইও।</span></>}>
        <AboutServiceGrid services={BN_ABOUT_SERVICES} headingLevel="h2" />
      </Section>

      <Section eyebrow="এআই সার্চ" title={<>এআই সার্চের জন্য <span className="text-gradient">AEO ও GEO।</span></>}>
        <AiSearchCards
          items={BN_AI_SEARCH_POINTS}
          intro="এখন মানুষ শুধু গুগলের সাধারণ ফলাফলে নয়, Google AI Overviews, ChatGPT, Perplexity ও Gemini-তেও খোঁজে। এসইওর পাশাপাশি আমি ব্যবসাগুলোকে এসব টুলে পরিষ্কার, উদ্ধৃতিযোগ্য উত্তর হয়ে উঠতে সাহায্য করি।"
          more="আরও জানুন"
        />
      </Section>

      <Section eyebrow="আমার পদ্ধতি" title={<>যেভাবে কাজটা <span className="text-gradient">ফল দেয়।</span></>}>
        <ApproachCards items={BN_APPROACH} headingLevel="h2" />
      </Section>

      <Section eyebrow="প্রশ্নোত্তর" title={<>সাধারণ <span className="text-gradient">প্রশ্ন।</span></>}>
        <FaqCardGrid items={BN_ABOUT_FAQ} headingLevel="h2" />
      </Section>

      <Section className="!pt-0">
        <CtaPanel
          title="আপনার সার্চ দৃশ্যমানতা উন্নত করতে প্রস্তুত?"
          body="আপনার সাইট, প্রতিযোগী এবং যেসব কীওয়ার্ড আসলেই রেভিনিউ আনে — সেগুলো নিয়ে কথা বলি।"
          primary={{ to: "/bn/book", label: "কল বুক করুন" }}
          secondary={{ to: "/bn/case-studies", label: "কেস স্টাডি দেখুন" }}
        />
      </Section>
    </>
  );
}
